import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Form, Offcanvas, Spinner } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import { opsAxiosInstance } from "../../api/axios";

// Same pattern as ChecklistOperations.jsx — hardcoded and visible in the
// bundle, so this route needs to be gated the same way. Worth pulling this
// (and the copy in ChecklistOperations.jsx) into one shared constants file
// so the two never drift out of sync.
const API_KEY = "sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC";
const API_SECRET = "79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD";

const ADMIN_HEADERS = {
    headers: {
        "X-API-Key": API_KEY,
        "X-API-Secret": API_SECRET,
    },
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const CHECKLIST_TYPE_OPTIONS = ["OPENING", "CLOSING", "GENERAL"];
const FREQUENCY_OPTIONS = ["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY"];

const normalizeData = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.outlets)) return payload.data.outlets;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
};

// "2026-07-15" -> "15 Jul 2026"
const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// "2026-07-15 10:00:14" -> "10:00 AM"
const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const d = new Date(String(dateTimeStr).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

// "2026-07-15 10:00:14" -> "15 Jul 2026, 10:00 AM"
const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "—";
    const d = new Date(String(dateTimeStr).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return dateTimeStr;
    return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

// Local YYYY-MM-DD (not toISOString(), which UTC-shifts and can roll the
// date back a day — same bug class noted in the Xpanse timezone fix).
const toISODate = (d) => {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

// ─── CSV export (client-side) ──────────────────────────────────────────────
// Cells that start with = + - @ would be run as formulas by Excel, so they
// get a leading apostrophe.
const csvCell = (value) => {
    let s = String(value ?? "");
    if (/^[=+\-@]/.test(s)) s = `'${s}`;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, rows) => {
    const body = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob([`﻿${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const initialOf = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";

// ─── Presentational pieces ─────────────────────────────────────────────────

const StatusPill = ({ status }) => {
    const isSubmitted = status === "SUBMITTED";
    return (
        <span className={`au-status ${isSubmitted ? "is-submitted" : "is-draft"}`}>
            <span className="au-dot" />
            {status === "SUBMITTED" ? "Submitted" : status === "DRAFT" ? "Draft" : status || "—"}
        </span>
    );
};

const AnswerBadge = ({ answer }) => {
    const normalized = (answer || "").toUpperCase();
    const isYes = normalized === "YES";
    const isNo = normalized === "NO";
    const cls = isYes ? "is-yes" : isNo ? "is-no" : "is-other";
    return (
        <span className={`au-answer-badge ${cls}`}>
            <i className={`bi ${isYes ? "bi-check-circle-fill" : isNo ? "bi-x-circle-fill" : "bi-question-circle"}`}></i>
            {answer || "—"}
        </span>
    );
};

// Outlet photo if the API provides one, otherwise a lettered tile.
const Thumb = ({ src, name, size = 46 }) => {
    const [broken, setBroken] = useState(false);
    return (
        <div className="au-thumb" style={{ width: size, height: size }}>
            {src && !broken ? (
                <img src={src} alt="" onError={() => setBroken(true)} />
            ) : (
                <span style={{ fontSize: size * 0.4 }}>{initialOf(name)}</span>
            )}
        </div>
    );
};

// Tiny area chart for the KPI cards. `values` is a short numeric series.
const Sparkline = ({ values, color, id }) => {
    const w = 130;
    const h = 46;
    const pts = values.length >= 2 ? values : [values[0] ?? 0, values[0] ?? 0];
    const max = Math.max(...pts, 1);
    const step = w / (pts.length - 1);
    const y = (v) => h - 6 - (v / max) * (h - 14);
    const coords = pts.map((v, i) => [i * step, y(v)]);
    // Smooth the line with cubic curves through horizontal midpoints.
    let d = `M ${coords[0][0]} ${coords[0][1]}`;
    for (let i = 1; i < coords.length; i += 1) {
        const [px, py] = coords[i - 1];
        const [cx, cy] = coords[i];
        const mx = (px + cx) / 2;
        d += ` C ${mx} ${py}, ${mx} ${cy}, ${cx} ${cy}`;
    }
    return (
        <svg className="au-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
            <defs>
                <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={`${d} L ${w} ${h} L 0 ${h} Z`} fill={`url(#spark-${id})`} />
            <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
};

// Shared react-select styling for the filter row.
const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: "38px",
        height: "38px",
        fontSize: "13px",
        borderRadius: 8,
        borderColor: state.isFocused ? "#3a57e8" : "#d0d5dd",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(58,87,232,.15)" : "none",
        "&:hover": { borderColor: state.isFocused ? "#3a57e8" : "#98a2b3" },
    }),
    valueContainer: (provided) => ({ ...provided, height: "38px", padding: "0 10px" }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: "none" }),
    indicatorsContainer: (provided) => ({ ...provided, height: "38px" }),
    singleValue: (provided) => ({ ...provided, color: "#344054" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (provided) => ({ ...provided, borderRadius: 8, overflow: "hidden" }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "13px",
        backgroundColor: state.isSelected ? "#3a57e8" : state.isFocused ? "#eef2ff" : "white",
        color: state.isSelected ? "white" : "#1d2939",
        cursor: "pointer",
    }),
};

// 1 … 4 5 6 … 20 style page list.
const buildPageList = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const set = new Set([1, total, current - 1, current, current + 1]);
    const list = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    list.forEach((p, i) => {
        if (i > 0 && p - list[i - 1] > 1) out.push("…");
        out.push(p);
    });
    return out;
};

const Audit = () => {
    const [loading, setLoading] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);

    const [outletFilter, setOutletFilter] = useState("all");
    const [checklistTypeFilter, setChecklistTypeFilter] = useState("all");
    const [frequencyFilter, setFrequencyFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [staffFilter, setStaffFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hasNoAnswersOnly, setHasNoAnswersOnly] = useState(false);
    // Searches the rows already loaded on the current page (the API has no
    // free-text search parameter).
    const [searchText, setSearchText] = useState("");

    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ page: 1, per_page: DEFAULT_PAGE_SIZE, total: 0, total_pages: 1 });
    const [selectedIds, setSelectedIds] = useState([]);

    // Detail drawer
    const [showDetail, setShowDetail] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null); // { submission, summary, categories }
    const [detailRow, setDetailRow] = useState(null); // the list row that opened the drawer
    const [answerFilter, setAnswerFilter] = useState("ALL");
    const [collapsed, setCollapsed] = useState({});

    // Guards against an older response overwriting a newer one.
    const listReq = useRef(0);
    const detailReq = useRef(0);
    const lastFilterKey = useRef(null);

    useEffect(() => {
        loadOutlets();
        loadStaff();
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterKey = [
        outletFilter, checklistTypeFilter, frequencyFilter, statusFilter, staffFilter,
        categoryFilter, toISODate(startDate), toISODate(endDate), hasNoAnswersOnly, pageSize,
    ].join("|");

    // Any filter change goes back to page 1 and is debounced so rapid
    // changes (e.g. picking both ends of a date range) don't fire a request
    // per click. A plain page change loads immediately.
    useEffect(() => {
        const filtersChanged = lastFilterKey.current !== null && lastFilterKey.current !== filterKey;
        lastFilterKey.current = filterKey;

        if (filtersChanged && page !== 1) {
            setPage(1);
            return undefined;
        }

        const handle = setTimeout(() => loadSubmissions(page), filtersChanged ? 300 : 0);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey, page]);

    const buildParams = (targetPage) => {
        const params = { page: targetPage, per_page: pageSize };
        if (outletFilter !== "all") params.outlet_id = outletFilter;
        if (checklistTypeFilter !== "all") params.checklist_type = checklistTypeFilter;
        if (frequencyFilter !== "all") params.frequency = frequencyFilter;
        if (statusFilter !== "all") params.status = statusFilter;
        if (staffFilter !== "all") params.staff_id = staffFilter;
        if (categoryFilter !== "all") params.category_id = categoryFilter;
        if (startDate) params.date_from = toISODate(startDate);
        if (endDate) params.date_to = toISODate(endDate);
        if (hasNoAnswersOnly) params.has_no_answers = true;
        return params;
    };

    const loadSubmissions = async (targetPage = page) => {
        const reqId = ++listReq.current;
        setLoading(true);
        try {
            const response = await opsAxiosInstance.get("/api/admin/checklist-submissions", {
                ...ADMIN_HEADERS,
                params: buildParams(targetPage),
            });
            if (reqId !== listReq.current) return;
            if (response?.data?.success) {
                setSubmissions(response.data.data?.submissions || []);
                setPagination(
                    response.data.data?.pagination || { page: targetPage, per_page: pageSize, total: 0, total_pages: 1 }
                );
                setSelectedIds([]);
            } else {
                toast.error(response?.data?.message || "Unable to load submission history.");
            }
        } catch (error) {
            if (reqId !== listReq.current) return;
            const message = error?.response?.data?.message || "Unable to load submission history.";
            toast.error(message);
        } finally {
            if (reqId === listReq.current) setLoading(false);
        }
    };

    const loadOutlets = async () => {
        try {
            const response = await opsAxiosInstance.get("/api/admin/outlets", ADMIN_HEADERS);
            setOutlets(normalizeData(response?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load outlets for filtering.";
            toast.error(message);
        }
    };

    // Same endpoint ChecklistOperations.jsx uses for its Staff tab.
    const loadStaff = async () => {
        try {
            const response = await opsAxiosInstance.get("/api/admin/staff", ADMIN_HEADERS);
            setStaffList(normalizeData(response?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load staff for filtering.";
            toast.error(message);
        }
    };

    // Same endpoint ChecklistOperations.jsx uses for its Categories tab.
    const loadCategories = async () => {
        try {
            const response = await opsAxiosInstance.get("/api/admin/checklist-categories", ADMIN_HEADERS);
            setCategoriesList(normalizeData(response?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load categories for filtering.";
            toast.error(message);
        }
    };

    const outletById = useMemo(() => {
        const map = {};
        outlets.forEach((o) => {
            map[String(o?.id ?? o?._id)] = o;
        });
        return map;
    }, [outlets]);

    const outletNameById = useMemo(() => {
        const map = {};
        Object.entries(outletById).forEach(([id, o]) => {
            map[id] = o?.outlet_name;
        });
        return map;
    }, [outletById]);

    const outletNameOf = (item) =>
        item?.outlet_name || outletNameById[String(item?.outlet_id)] || `Outlet #${item?.outlet_id}`;

    // Outlet photo from the submission row, falling back to the outlet record.
    const outletImageOf = (item) => {
        const o = outletById[String(item?.outlet_id)];
        return item?.outlet_image || item?.outlet_image_url || o?.image_url || o?.image || o?.logo || o?.photo || "";
    };

    const outletCodeOf = (item) => {
        const o = outletById[String(item?.outlet_id)];
        return item?.outlet_code || o?.outlet_code || (item?.outlet_id ? `#${item.outlet_id}` : "");
    };

    // ─── Drawer ─────────────────────────────────────────────────────────────
    const viewSubmission = async (row) => {
        const id = row?.id;
        const reqId = ++detailReq.current;
        setDetailRow(row);
        setShowDetail(true);
        setDetailLoading(true);
        setDetail(null);
        setAnswerFilter("ALL");
        setCollapsed({});
        try {
            const response = await opsAxiosInstance.get(`/api/admin/checklist-submissions/${id}`, ADMIN_HEADERS);
            if (reqId !== detailReq.current) return;
            if (response?.data?.success) {
                setDetail(response.data.data);
            } else {
                toast.error(response?.data?.message || "Unable to load this submission.");
                setShowDetail(false);
            }
        } catch (error) {
            if (reqId !== detailReq.current) return;
            const message = error?.response?.data?.message || "Unable to load this submission.";
            toast.error(message);
            setShowDetail(false);
        } finally {
            if (reqId === detailReq.current) setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        detailReq.current += 1;
        setShowDetail(false);
    };

    const detailIndex = detailRow ? submissions.findIndex((s) => s?.id === detailRow?.id) : -1;
    const stepDetail = (delta) => {
        const next = submissions[detailIndex + delta];
        if (next) viewSubmission(next);
    };

    // ─── Derived list data ──────────────────────────────────────────────────
    const visibleRows = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return submissions;
        return submissions.filter((item) =>
            [outletNameOf(item), item?.checklist_type, item?.frequency, item?.staff_name, item?.address, outletCodeOf(item)]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissions, searchText, outletById]);

    // NOTE: these only reflect the currently loaded page, not the full filtered
    // result set — the API doesn't return a global status breakdown, only a
    // total record count via pagination.total.
    const submittedCountOnPage = useMemo(
        () => submissions.filter((s) => s?.status === "SUBMITTED").length,
        [submissions]
    );
    const draftCountOnPage = useMemo(
        () => submissions.filter((s) => s?.status === "DRAFT").length,
        [submissions]
    );

    // Records-per-business-date for the page, used only to draw the small
    // trend lines on the KPI cards.
    const sparkSeries = (predicate) => {
        const byDate = {};
        submissions.forEach((s) => {
            const key = String(s?.business_date || "").slice(0, 10);
            if (!key) return;
            byDate[key] = (byDate[key] || 0) + (predicate(s) ? 1 : 0);
        });
        const values = Object.keys(byDate).sort().map((k) => byDate[k]);
        return values.length ? values : [0];
    };

    const totalRecords = pagination.total ?? 0;
    const pct = (n) => (submissions.length ? Math.round((n / submissions.length) * 100) : 0);

    const summaryCards = [
        {
            id: "total", title: "Total Records", value: totalRecords, icon: "bi-file-earmark-text",
            color: "#3a57e8", tone: "blue", spark: sparkSeries(() => true),
            sub: `${pagination.total_pages || 1} page${(pagination.total_pages || 1) === 1 ? "" : "s"} · ${pagination.per_page || pageSize} per page`,
        },
        {
            id: "submitted", title: "Submitted (Page)", value: submittedCountOnPage, icon: "bi-check2-circle",
            color: "#12b76a", tone: "green", spark: sparkSeries((s) => s?.status === "SUBMITTED"),
            sub: `${pct(submittedCountOnPage)}% of this page`,
        },
        {
            id: "drafts", title: "Drafts (Page)", value: draftCountOnPage, icon: "bi-pencil-square",
            color: "#f79009", tone: "orange", spark: sparkSeries((s) => s?.status === "DRAFT"),
            sub: `${pct(draftCountOnPage)}% of this page`,
        },
    ];

    const totalPages = pagination.total_pages || 1;
    const safePage = pagination.page || page;
    const rangeStart = totalRecords === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const rangeEnd = Math.min(safePage * pageSize, totalRecords);

    const activeFilterCount = [
        outletFilter, checklistTypeFilter, frequencyFilter, statusFilter, staffFilter, categoryFilter,
    ].filter((v) => v !== "all").length + (startDate || endDate ? 1 : 0) + (hasNoAnswersOnly ? 1 : 0) + (searchText.trim() ? 1 : 0);

    const resetFilters = () => {
        setOutletFilter("all");
        setChecklistTypeFilter("all");
        setFrequencyFilter("all");
        setStatusFilter("all");
        setStaffFilter("all");
        setCategoryFilter("all");
        setStartDate(null);
        setEndDate(null);
        setHasNoAnswersOnly(false);
        setSearchText("");
    };

    // ─── Selection + export ─────────────────────────────────────────────────
    const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.includes(r?.id));
    const toggleAll = () => {
        setSelectedIds(allVisibleSelected ? [] : visibleRows.map((r) => r?.id));
    };
    const toggleOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const rowToCsv = (item) => [
        formatDate(item?.business_date),
        outletNameOf(item),
        outletCodeOf(item),
        item?.checklist_type || "",
        item?.frequency || "",
        item?.staff_name || "",
        item?.address || "",
        item?.status === "DRAFT" ? "" : formatDateTime(item?.submitted_at),
        item?.yes_count ?? "",
        item?.no_count ?? "",
        item?.total_answers ?? "",
        item?.status || "",
    ];

    const CSV_HEAD = [
        "Business date", "Outlet", "Outlet ID", "Type", "Frequency", "Submitted by",
        "Location", "Submitted at", "Yes", "No", "Total answers", "Status",
    ];

    // Exports the ticked rows, or every row currently shown if none are ticked.
    // This is the loaded page only — the API has no export endpoint here.
    const exportRows = (rows) => {
        if (!rows.length) {
            toast.info("Nothing to export.");
            return;
        }
        downloadCsv(`audit-${toISODate(new Date())}.csv`, [CSV_HEAD, ...rows.map(rowToCsv)]);
        toast.success(`Exported ${rows.length} record${rows.length === 1 ? "" : "s"}.`);
    };

    const handleExport = () => {
        const picked = visibleRows.filter((r) => selectedIds.includes(r?.id));
        exportRows(picked.length ? picked : visibleRows);
    };

    const exportDetail = () => {
        if (!detail) return;
        const sub = detail.submission || {};
        const rows = [["Category", "Task", "Answer", "Remark"]];
        (detail.categories || []).forEach((g) =>
            (g.answers || []).forEach((a) => rows.push([g.category_name, a?.task_name, a?.answer, a?.remark || ""]))
        );
        downloadCsv(`audit-${sub?.id ?? "submission"}-${toISODate(new Date())}.csv`, rows);
        toast.success("Submission exported.");
    };

    // ─── react-select option lists ──────────────────────────────────────────
    const outletOptions = useMemo(
        () => [
            { value: "all", label: "All outlets" },
            ...outlets.map((o) => ({ value: String(o?.id ?? o?._id), label: o?.outlet_name })),
        ],
        [outlets]
    );
    const typeOptions = [
        { value: "all", label: "All types" },
        ...CHECKLIST_TYPE_OPTIONS.map((t) => ({ value: t, label: t })),
    ];
    const frequencyOptions = [
        { value: "all", label: "All frequencies" },
        ...FREQUENCY_OPTIONS.map((f) => ({ value: f, label: f })),
    ];
    const statusOptions = [
        { value: "all", label: "All status" },
        { value: "SUBMITTED", label: "Submitted" },
        { value: "DRAFT", label: "Draft" },
    ];
    const staffOptions = useMemo(
        () => [
            { value: "all", label: "All staff" },
            ...staffList.map((s) => ({
                value: String(s?.id ?? s?._id),
                label: s?.outlet_name ? `${s?.staff_name} · ${s.outlet_name}` : s?.staff_name,
            })),
        ],
        [staffList]
    );
    const categoryOptions = useMemo(
        () => [
            { value: "all", label: "All categories" },
            ...categoriesList.map((c) => ({ value: String(c?.id ?? c?._id), label: c?.category_name })),
        ],
        [categoriesList]
    );

    const findOption = (options, value) => options.find((o) => o.value === value) || options[0];

    const filterSelects = [
        { key: "outlet", label: "Outlet", icon: "bi-shop", options: outletOptions, value: outletFilter, set: setOutletFilter, searchable: true },
        { key: "type", label: "Type", icon: "bi-tag", options: typeOptions, value: checklistTypeFilter, set: setChecklistTypeFilter },
        { key: "frequency", label: "Frequency", icon: "bi-arrow-repeat", options: frequencyOptions, value: frequencyFilter, set: setFrequencyFilter },
        { key: "status", label: "Status", icon: "bi-activity", options: statusOptions, value: statusFilter, set: setStatusFilter },
        { key: "staff", label: "Staff Name", icon: "bi-person", options: staffOptions, value: staffFilter, set: setStaffFilter, searchable: true, placeholder: "All staff" },
        { key: "category", label: "Category", icon: "bi-grid", options: categoryOptions, value: categoryFilter, set: setCategoryFilter, searchable: true, placeholder: "All categories" },
    ];

    // ─── Drawer data ────────────────────────────────────────────────────────
    const sub = detail?.submission;
    const summary = detail?.summary;
    const yesTotal = summary?.yes_count ?? 0;
    const noTotal = summary?.no_count ?? 0;
    const allTotal = summary?.total_answers ?? 0;
    const yesRate = allTotal ? Math.round((yesTotal / allTotal) * 100) : 0;

    const drawerGroups = useMemo(() => {
        const groups = detail?.categories || [];
        return groups
            .map((g) => {
                const answers = (g.answers || []).filter((a) => {
                    if (answerFilter === "ALL") return true;
                    return (a?.answer || "").toUpperCase() === answerFilter;
                });
                return { ...g, shown: answers };
            })
            .filter((g) => g.shown.length > 0);
    }, [detail, answerFilter]);

    const toggleGroup = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
    const allCollapsed = drawerGroups.length > 0 && drawerGroups.every((g) => collapsed[g.category_id]);
    const setAllCollapsed = (value) => {
        const next = {};
        (detail?.categories || []).forEach((g) => { next[g.category_id] = value; });
        setCollapsed(next);
    };

    const drawerName = detailRow ? outletNameOf(detailRow) : "Submission";
    const drawerImg = detailRow ? outletImageOf(detailRow) : "";

    return (
        <React.Fragment>
            <Header />
            <style>{auStyles}</style>
            <div className="main main-app au-page">

                {/* ── Banner ── */}
                <div className="au-banner">
                    <div className="au-banner-left">
                        <div className="au-banner-icon"><i className="bi bi-clipboard-check" /></div>
                        <div>
                            <div className="au-eyebrow">Checklist operations</div>
                            <h4 className="au-title">Audit</h4>
                            <p className="au-subtitle">History of completed and in-progress checklist submissions across outlets.</p>
                        </div>
                    </div>
                    <div className="au-banner-art" aria-hidden="true">
                        <div className="au-art-sheet"><span /><span /><span /><span /></div>
                    </div>
                    <Button className="au-btn au-btn-ghost" onClick={() => loadSubmissions(page)} disabled={loading}>
                        <i className={`bi bi-arrow-clockwise ${loading ? "au-spin" : ""}`} /> Refresh
                    </Button>
                </div>

                {/* ── KPI cards ── */}
                <div className="au-stats">
                    {summaryCards.map((card) => (
                        <div key={card.id} className={`au-stat tone-${card.tone}`}>
                            <div className="au-stat-icon"><i className={`bi ${card.icon}`} /></div>
                            <div className="au-stat-body">
                                <div className="au-stat-label">{card.title}</div>
                                <div className="au-stat-value">{card.value}</div>
                                <div className="au-stat-sub">{card.sub}</div>
                            </div>
                            <Sparkline values={card.spark} color={card.color} id={card.id} />
                        </div>
                    ))}
                </div>

                {/* ── Filters ── */}
                <div className="au-panel">
                    <div className="au-filter-grid">
                        <div className="au-search">
                            <i className="bi bi-search" />
                            <input
                                type="text"
                                placeholder="Search by outlet, type, staff name..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                aria-label="Search loaded submissions"
                            />
                            {searchText && (
                                <button type="button" aria-label="Clear search" onClick={() => setSearchText("")}><i className="bi bi-x-lg" /></button>
                            )}
                        </div>
                        {filterSelects.map((f) => (
                            <div className="au-select-wrap" key={f.key}>
                                <Form.Label className="au-label"><i className={`bi ${f.icon}`} /> {f.label}</Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={f.options}
                                    value={findOption(f.options, f.value)}
                                    onChange={(opt) => f.set(opt?.value || "all")}
                                    isSearchable={!!f.searchable}
                                    placeholder={f.placeholder}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                        ))}
                    </div>

                    <div className="au-filter-row2">
                        <div className="au-date-wrap">
                            <i className="bi bi-calendar3 au-date-icon"></i>
                            <DatePicker
                                className="au-datepicker-input"
                                selectsRange
                                startDate={startDate}
                                endDate={endDate}
                                onChange={(update) => {
                                    const [start, end] = update;
                                    setStartDate(start);
                                    setEndDate(end);
                                }}
                                dateFormat="dd MMM yyyy"
                                placeholderText="Business date range"
                                isClearable={false}
                                popperPlacement="bottom-start"
                            />
                            {(startDate || endDate) ? (
                                <button
                                    type="button"
                                    className="au-date-clear"
                                    title="Clear dates"
                                    aria-label="Clear dates"
                                    onClick={() => {
                                        setStartDate(null);
                                        setEndDate(null);
                                    }}
                                >
                                    <i className="bi bi-x-lg" />
                                </button>
                            ) : (
                                <i className="bi bi-chevron-down au-date-chev" />
                            )}
                        </div>
                        <label className="au-switch">
                            <input
                                type="checkbox"
                                checked={hasNoAnswersOnly}
                                onChange={(e) => setHasNoAnswersOnly(e.target.checked)}
                            />
                            Has NO answers only
                        </label>

                        <div className="au-filter-actions">
                            <Button className="au-btn au-btn-soft" onClick={resetFilters} disabled={activeFilterCount === 0}>
                                <i className="bi bi-arrow-counterclockwise" /> Reset
                            </Button>
                            <Button className="au-btn au-btn-primary" onClick={handleExport} disabled={visibleRows.length === 0}>
                                <i className="bi bi-download" /> Export{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* ── Table ── */}
                <div className="au-panel au-table-panel">
                    {loading && submissions.length === 0 ? (
                        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                    ) : (
                        <div className={`au-tablewrap ${loading ? "is-loading" : ""}`}>
                            <table className="au-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 38 }}>
                                            <input type="checkbox" className="au-check" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all rows" />
                                        </th>
                                        <th>Business date</th>
                                        <th>Outlet</th>
                                        <th>Type / Frequency</th>
                                        <th>Submitted by</th>
                                        <th>Location</th>
                                        <th>Submitted at</th>
                                        <th>Answers</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.length === 0 && (
                                        <tr>
                                            <td colSpan={10}>
                                                <div className="au-empty">
                                                    <div className="au-empty-icon"><i className="bi bi-clipboard-x" /></div>
                                                    <div>No submissions match the current filters.</div>
                                                    {activeFilterCount > 0 && (
                                                        <button type="button" className="au-linkbtn" onClick={resetFilters}>Clear filters</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {visibleRows.map((item) => {
                                        const outletName = outletNameOf(item);
                                        const picked = selectedIds.includes(item?.id);
                                        const timeSource = item?.created_at || item?.started_at || (item?.status === "DRAFT" ? null : item?.submitted_at);
                                        const time = formatTime(timeSource);
                                        return (
                                            <tr key={item?.id} className={`au-row ${picked ? "is-picked" : ""}`} onClick={() => viewSubmission(item)}>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <input type="checkbox" className="au-check" checked={picked} onChange={() => toggleOne(item?.id)} aria-label="Select row" />
                                                </td>
                                                <td>
                                                    <div className="au-cell-strong">{formatDate(item?.business_date)}</div>
                                                    {time && <div className="au-subtext">{time}</div>}
                                                </td>
                                                <td>
                                                    <div className="au-outlet-cell">
                                                        <Thumb src={outletImageOf(item)} name={outletName} />
                                                        <div className="au-outlet-text">
                                                            <div className="au-cell-strong" title={outletName}>{outletName}</div>
                                                            <div className="au-subtext">Outlet ID: {outletCodeOf(item) || "—"}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="au-type-pill">{item?.checklist_type || "—"}</span>
                                                    <div className="au-subtext mt-1">{item?.frequency}</div>
                                                </td>
                                                <td>
                                                    <div className="au-person">
                                                        <div className="au-avatar">{initialOf(item?.staff_name)}</div>
                                                        <div>
                                                            <div className="au-cell-strong">{item?.staff_name || "—"}</div>
                                                            {item?.staff_name && <div className="au-subtext">{item?.staff_role || "Staff"}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="au-address-cell" title={item?.address}>
                                                    {item?.address ? (<><i className="bi bi-geo-alt-fill" /><span>{item.address}</span></>) : "—"}
                                                </td>
                                                <td>{item?.status === "DRAFT" ? "—" : formatDateTime(item?.submitted_at)}</td>
                                                <td>
                                                    {item?.total_answers === undefined || item?.total_answers === null ? (
                                                        <span className="au-subtext">—</span>
                                                    ) : (
                                                        <div className="au-answer-counts">
                                                            <span className="au-count-yes">{item?.yes_count ?? 0} Yes</span>
                                                            <span className="au-count-no">{item?.no_count ?? 0} No</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td><StatusPill status={item?.status} /></td>
                                                <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                    <div className="au-actions">
                                                        <button type="button" className="au-view-btn" onClick={() => viewSubmission(item)}>
                                                            View details
                                                        </button>
                                                        <Dropdown align="end">
                                                            <Dropdown.Toggle variant="link" className="au-kebab" aria-label="More actions">
                                                                <i className="bi bi-three-dots-vertical" />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} className="au-menu">
                                                                <Dropdown.Item onClick={() => viewSubmission(item)}><i className="bi bi-eye" /> View details</Dropdown.Item>
                                                                <Dropdown.Item onClick={() => exportRows([item])}><i className="bi bi-download" /> Export this record</Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="au-pagination">
                        <span className="au-pagination-info">
                            Showing {rangeStart} to {rangeEnd} of {totalRecords} record{totalRecords === 1 ? "" : "s"}
                            {searchText.trim() && visibleRows.length !== submissions.length ? ` · ${visibleRows.length} match your search on this page` : ""}
                        </span>
                        <div className="au-pagination-controls">
                            <button type="button" className="au-pg" aria-label="Previous page" disabled={safePage <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                <i className="bi bi-chevron-left" />
                            </button>
                            {buildPageList(safePage, totalPages).map((p, i) => (
                                p === "…" ? (
                                    <span key={`gap-${i}`} className="au-pg-gap">…</span>
                                ) : (
                                    <button key={p} type="button" className={`au-pg ${p === safePage ? "is-on" : ""}`} disabled={loading} onClick={() => setPage(p)}>{p}</button>
                                )
                            ))}
                            <button type="button" className="au-pg" aria-label="Next page" disabled={safePage >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                                <i className="bi bi-chevron-right" />
                            </button>
                            <Form.Select className="au-pagesize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                                {PAGE_SIZE_OPTIONS.map((n) => (<option key={n} value={n}>{n} / page</option>))}
                            </Form.Select>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Detail drawer ── */}
            <Offcanvas show={showDetail} onHide={closeDetail} placement="end" className="au-drawer">
                <div className="au-drawer-head">
                    <div className="au-drawer-headmain">
                        <Thumb src={drawerImg} name={drawerName} size={48} />
                        <div className="au-drawer-headtext">
                            <Offcanvas.Title as="div" className="au-drawer-title">{drawerName}</Offcanvas.Title>
                            <div className="au-drawer-sub">
                                {detail ? (
                                    <>
                                        <span className="au-type-pill">{sub?.checklist_type}</span>
                                        <span>{sub?.frequency}</span>
                                        <span className="au-sep">•</span>
                                        <span>{formatDate(sub?.business_date)}</span>
                                    </>
                                ) : (
                                    <span>{detailLoading ? "Loading submission…" : "Submission"}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="au-drawer-headactions">
                        <button type="button" className="au-ibtn" title="Previous record" aria-label="Previous record" disabled={detailIndex <= 0 || detailLoading} onClick={() => stepDetail(-1)}>
                            <i className="bi bi-chevron-up" />
                        </button>
                        <button type="button" className="au-ibtn" title="Next record" aria-label="Next record" disabled={detailIndex < 0 || detailIndex >= submissions.length - 1 || detailLoading} onClick={() => stepDetail(1)}>
                            <i className="bi bi-chevron-down" />
                        </button>
                        <button type="button" className="au-ibtn" title="Close" aria-label="Close" onClick={closeDetail}>
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>
                </div>

                <Offcanvas.Body className="au-drawer-body">
                    {detailLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                    ) : !detail ? (
                        <div className="au-empty">Unable to load this submission.</div>
                    ) : (
                        <>
                            <div className="au-drawer-status">
                                <StatusPill status={sub?.status} />
                                <span className="au-subtext">
                                    {sub?.status === "DRAFT" ? "Not yet submitted" : `Submitted ${formatDateTime(sub?.submitted_at)}`}
                                </span>
                            </div>

                            <div className="au-summary-strip">
                                <div className="au-summary-pill">
                                    <div className="au-summary-value">{allTotal}</div>
                                    <div className="au-summary-label">Total answers</div>
                                </div>
                                <div className="au-summary-pill is-yes">
                                    <div className="au-summary-value">{yesTotal}</div>
                                    <div className="au-summary-label">Yes</div>
                                </div>
                                <div className="au-summary-pill is-no">
                                    <div className="au-summary-value">{noTotal}</div>
                                    <div className="au-summary-label">No</div>
                                </div>
                            </div>

                            {allTotal > 0 && (
                                <div className="au-rate">
                                    <div className="au-rate-head"><span>Yes rate</span><strong>{yesRate}%</strong></div>
                                    <div className="au-rate-bar"><span style={{ width: `${yesRate}%` }} /></div>
                                </div>
                            )}

                            <div className="au-meta-card">
                                <div className="au-meta-row">
                                    <i className="bi bi-person au-meta-icon"></i>
                                    <div>
                                        <div className="au-meta-label">Submitted by</div>
                                        <div className="au-meta-value">{sub?.submitted_by_name || detailRow?.staff_name || "—"}</div>
                                    </div>
                                </div>
                                <div className="au-meta-row">
                                    <i className="bi bi-calendar3 au-meta-icon"></i>
                                    <div>
                                        <div className="au-meta-label">Business date</div>
                                        <div className="au-meta-value">{formatDate(sub?.business_date)}</div>
                                    </div>
                                </div>
                                <div className="au-meta-row">
                                    <i className="bi bi-clock au-meta-icon"></i>
                                    <div>
                                        <div className="au-meta-label">Submitted at</div>
                                        <div className="au-meta-value">{sub?.status === "DRAFT" ? "Not yet submitted" : formatDateTime(sub?.submitted_at)}</div>
                                    </div>
                                </div>
                                <div className="au-meta-row">
                                    <i className="bi bi-hash au-meta-icon"></i>
                                    <div>
                                        <div className="au-meta-label">Outlet ID</div>
                                        <div className="au-meta-value">{outletCodeOf({ ...detailRow, ...sub }) || "—"}</div>
                                    </div>
                                </div>
                                <div className="au-meta-row is-wide">
                                    <i className="bi bi-geo-alt au-meta-icon"></i>
                                    <div>
                                        <div className="au-meta-label">Location</div>
                                        <div className="au-meta-value">
                                            {sub?.address || "—"}
                                            {sub?.latitude && sub?.longitude && (
                                                <a
                                                    className="au-map-link"
                                                    href={`https://www.google.com/maps?q=${sub.latitude},${sub.longitude}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View on map <i className="bi bi-box-arrow-up-right"></i>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="au-answers-bar">
                                <div className="au-seg" role="radiogroup" aria-label="Filter answers">
                                    {[
                                        { v: "ALL", label: "All", n: allTotal },
                                        { v: "YES", label: "Yes", n: yesTotal },
                                        { v: "NO", label: "No", n: noTotal },
                                    ].map((o) => (
                                        <button key={o.v} type="button" role="radio" aria-checked={answerFilter === o.v} className={`${answerFilter === o.v ? "is-on" : ""} ${o.v === "NO" ? "is-no" : o.v === "YES" ? "is-yes" : ""}`} onClick={() => setAnswerFilter(o.v)}>
                                            {o.label} <span>{o.n}</span>
                                        </button>
                                    ))}
                                </div>
                                {drawerGroups.length > 0 && (
                                    <button type="button" className="au-linkbtn" onClick={() => setAllCollapsed(!allCollapsed)}>
                                        {allCollapsed ? "Expand all" : "Collapse all"}
                                    </button>
                                )}
                            </div>

                            {(!detail?.categories || detail.categories.length === 0) ? (
                                <div className="au-empty">No task answers recorded for this submission.</div>
                            ) : drawerGroups.length === 0 ? (
                                <div className="au-empty">No {answerFilter === "YES" ? "Yes" : "No"} answers in this submission.</div>
                            ) : (
                                drawerGroups.map((group) => {
                                    const isClosed = !!collapsed[group.category_id];
                                    return (
                                        <div key={group.category_id} className="au-answer-group">
                                            <button type="button" className="au-answer-group-head" aria-expanded={!isClosed} onClick={() => toggleGroup(group.category_id)}>
                                                <i className={`bi ${isClosed ? "bi-chevron-right" : "bi-chevron-down"}`} />
                                                <span className="au-answer-group-title">{group.category_name}</span>
                                                <span className="au-group-badges">
                                                    <span className="au-group-badge is-yes">{group.yes_count ?? 0} Yes</span>
                                                    <span className="au-group-badge is-no">{group.no_count ?? 0} No</span>
                                                </span>
                                            </button>
                                            {!isClosed && (
                                                <div className="au-answer-list">
                                                    {group.shown.map((a) => (
                                                        <div key={a?.task_id} className={`au-answer-row ${(a?.answer || "").toUpperCase() === "NO" ? "is-no" : ""}`}>
                                                            <div className="au-answer-task">
                                                                <div>{a?.task_name}</div>
                                                                {a?.remark && (
                                                                    <div className="au-answer-remark"><i className="bi bi-chat-left-text" /> {a.remark}</div>
                                                                )}
                                                            </div>
                                                            <AnswerBadge answer={a?.answer} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </>
                    )}
                </Offcanvas.Body>

                <div className="au-drawer-foot">
                    <Button className="au-btn au-btn-soft" onClick={closeDetail}>Close</Button>
                    <Button className="au-btn au-btn-primary" onClick={exportDetail} disabled={!detail}>
                        <i className="bi bi-download" /> Export
                    </Button>
                </div>
            </Offcanvas>
        </React.Fragment>
    );
};

const auStyles = `
  .au-page { --au-primary:#3a57e8; --au-primary-dark:#2b44c4; --au-ink:#1d2939; --au-muted:#667085; --au-line:#e4e7ec; padding:18px 24px 40px; background:#f5f6fa; }

  /* Banner */
  .au-banner { position:relative; display:flex; align-items:center; gap:16px; padding:22px 24px; border-radius:10px; margin-bottom:16px; overflow:hidden; color:#fff; background:linear-gradient(110deg,#0d1b4c 0%,#16296b 55%,#1f3a8a 100%); }
  .au-banner::after { content:""; position:absolute; inset:0; background:radial-gradient(circle at 78% 20%, rgba(120,150,255,.28), transparent 45%); pointer-events:none; }
  .au-banner-left { position:relative; z-index:1; display:flex; align-items:center; gap:16px; flex:1; min-width:0; }
  .au-banner-icon { width:58px; height:58px; flex-shrink:0; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:26px; color:#fff; background:linear-gradient(135deg,#3a57e8,#5b78ff); box-shadow:0 8px 20px rgba(58,87,232,.45); }
  .au-eyebrow { font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:#b9c6ff; font-weight:600; margin-bottom:2px; }
  .au-title { font-weight:700; color:#fff; margin:0 0 2px; font-size:24px; letter-spacing:-.3px; }
  .au-subtitle { color:#d3dbff; font-size:13px; margin:0; }
  .au-banner-art { position:relative; z-index:1; width:200px; height:70px; flex-shrink:0; }
  .au-art-sheet { position:absolute; right:30px; top:-18px; width:96px; height:110px; border-radius:8px; border:1px solid rgba(255,255,255,.25); background:linear-gradient(160deg,rgba(255,255,255,.22),rgba(255,255,255,.05)); padding:20px 14px; display:flex; flex-direction:column; gap:10px; transform:perspective(400px) rotateY(-14deg) rotateZ(4deg); }
  .au-art-sheet span { height:6px; border-radius:3px; background:rgba(200,214,255,.6); }
  .au-art-sheet span:nth-child(odd) { width:70%; }
  .au-spin { display:inline-block; animation:au-rot 1s linear infinite; }
  @keyframes au-rot { to { transform:rotate(360deg); } }

  /* Buttons */
  .au-btn.btn { height:38px; padding:0 16px; font-size:13.5px; font-weight:600; border-radius:8px; display:inline-flex; align-items:center; justify-content:center; gap:8px; white-space:nowrap; }
  .au-btn-primary.btn { background:var(--au-primary); border-color:var(--au-primary); color:#fff; }
  .au-btn-primary.btn:hover, .au-btn-primary.btn:focus, .au-btn-primary.btn:active { background:var(--au-primary-dark) !important; border-color:var(--au-primary-dark) !important; color:#fff; }
  .au-btn-primary.btn:disabled { background:var(--au-primary); border-color:var(--au-primary); opacity:.55; }
  .au-btn-soft.btn { background:#f2f4f7; border-color:#f2f4f7; color:#344054; }
  .au-btn-soft.btn:hover { background:#e4e7ec; border-color:#e4e7ec; color:#1d2939; }
  .au-btn-soft.btn:disabled { opacity:.55; }
  .au-btn-ghost.btn { position:relative; z-index:1; color:#fff; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.4); }
  .au-btn-ghost.btn:hover, .au-btn-ghost.btn:focus { color:#fff; background:rgba(255,255,255,.16); border-color:rgba(255,255,255,.7); }
  .au-linkbtn { background:none; border:0; padding:0; font-size:12.5px; font-weight:600; color:var(--au-primary); cursor:pointer; }
  .au-linkbtn:hover { text-decoration:underline; }

  /* KPI cards */
  .au-stats { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:16px; margin-bottom:16px; }
  .au-stat { position:relative; display:flex; align-items:center; gap:14px; background:#fff; border:1px solid var(--au-line); border-radius:10px; padding:16px 18px; overflow:hidden; }
  .au-stat-icon { width:50px; height:50px; flex-shrink:0; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:22px; }
  .au-stat-body { position:relative; z-index:1; min-width:0; }
  .au-stat-label { font-size:14px; font-weight:600; color:#475467; }
  .au-stat-value { font-size:28px; font-weight:700; color:var(--au-ink); line-height:1.15; margin:2px 0; }
  .au-stat-sub { font-size:12px; color:var(--au-muted); }
  .au-spark { position:absolute; right:10px; bottom:6px; width:140px; height:56px; }
  .tone-blue .au-stat-icon { background:#e8eeff; color:#3a57e8; }
  .tone-green .au-stat-icon { background:#dff7ea; color:#0f9d58; }
  .tone-orange .au-stat-icon { background:#ffefdc; color:#ea8a00; }

  /* Panels */
  .au-panel { background:#fff; border:1px solid var(--au-line); border-radius:10px; padding:16px; margin-bottom:16px; }
  .au-filter-grid { display:grid; grid-template-columns:minmax(220px,2.4fr) repeat(6,minmax(112px,1fr)); gap:12px; align-items:end; }
  .au-search { position:relative; }
  .au-search > i { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#667085; font-size:14px; }
  .au-search input { width:100%; height:38px; border:1px solid #d0d5dd; border-radius:8px; padding:0 30px 0 36px; font-size:13px; outline:none; background:#fff; }
  .au-search input:focus { border-color:var(--au-primary); box-shadow:0 0 0 3px rgba(58,87,232,.15); }
  .au-search button { position:absolute; right:6px; top:50%; transform:translateY(-50%); border:0; background:none; color:#98a2b3; font-size:11px; padding:4px; }
  .au-select-wrap { min-width:0; }
  .au-label { display:block; font-size:12px; font-weight:500; color:var(--au-muted); margin-bottom:5px; }
  .au-label i { margin-right:5px; }

  .au-filter-row2 { display:flex; flex-wrap:wrap; align-items:center; gap:16px; margin-top:14px; }
  .au-date-wrap { position:relative; display:flex; align-items:center; }
  .au-date-wrap .react-datepicker-wrapper { width:auto; }
  .au-date-icon { position:absolute; left:12px; font-size:14px; color:#667085; pointer-events:none; z-index:1; }
  .au-date-chev { position:absolute; right:12px; font-size:12px; color:#667085; pointer-events:none; }
  .au-datepicker-input { width:250px; height:38px; font-size:13px; padding:0 30px 0 36px; border:1px solid #d0d5dd; border-radius:8px; color:#1d2939; background:#fff; }
  .au-datepicker-input:focus { border-color:var(--au-primary); box-shadow:0 0 0 3px rgba(58,87,232,.15); outline:none; }
  .au-datepicker-input::placeholder { color:#98a2b3; }
  .react-datepicker-popper { z-index:1060; }
  .au-date-clear { position:absolute; right:8px; background:none; border:0; color:#98a2b3; font-size:11px; padding:4px; cursor:pointer; }
  .au-date-clear:hover { color:#d92d20; }
  .au-switch { display:flex; align-items:center; gap:8px; font-size:13px; color:#344054; white-space:nowrap; cursor:pointer; margin:0; }
  .au-switch input, .au-check { width:16px; height:16px; cursor:pointer; accent-color:var(--au-primary); }
  .au-filter-actions { margin-left:auto; display:flex; gap:10px; }

  /* Table */
  .au-table-panel { padding:6px 6px 14px; }
  .au-tablewrap { overflow-x:auto; transition:opacity .15s; }
  .au-tablewrap.is-loading { opacity:.55; pointer-events:none; }
  .au-table { width:100%; border-collapse:collapse; min-width:1100px; }
  .au-table thead th { font-size:11px; text-transform:uppercase; letter-spacing:.05em; color:#667085; font-weight:600; padding:14px 12px; border-bottom:1px solid var(--au-line); white-space:nowrap; }
  .au-table td { padding:12px; font-size:13px; color:#344054; border-bottom:1px solid #f0f2f5; vertical-align:middle; }
  .au-table tbody tr:last-child td { border-bottom:0; }
  .au-row { cursor:pointer; transition:background .12s; }
  .au-row:hover { background:#fafbff; }
  .au-row.is-picked { background:#f3f6ff; }
  .au-cell-strong { font-weight:600; color:var(--au-ink); }
  .au-subtext { font-size:11.5px; color:#98a2b3; }
  .au-outlet-cell { display:flex; align-items:center; gap:12px; min-width:170px; }
  .au-outlet-text { min-width:0; }
  .au-outlet-text .au-cell-strong { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:160px; }
  .au-thumb { flex-shrink:0; border-radius:8px; overflow:hidden; background:linear-gradient(135deg,#e8eeff,#d5defc); display:flex; align-items:center; justify-content:center; color:var(--au-primary); font-weight:700; }
  .au-thumb img { width:100%; height:100%; object-fit:cover; }
  .au-type-pill { display:inline-block; font-size:10.5px; font-weight:700; letter-spacing:.04em; color:var(--au-primary); background:#eef2ff; border:1px solid #c7d2fe; border-radius:20px; padding:2px 10px; }
  .au-person { display:flex; align-items:center; gap:10px; }
  .au-avatar { width:32px; height:32px; border-radius:50%; background:#7b8cb8; color:#fff; font-size:13px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .au-address-cell { max-width:190px; }
  .au-address-cell i { color:var(--au-primary); margin-right:6px; float:left; margin-top:2px; }
  .au-address-cell span { display:block; overflow:hidden; text-overflow:ellipsis; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; color:#667085; }
  .au-answer-counts { display:flex; flex-direction:column; gap:2px; font-size:12.5px; font-weight:600; white-space:nowrap; }
  .au-count-yes { color:#12a05c; }
  .au-count-no { color:#d92d20; }
  .au-status { display:inline-flex; align-items:center; gap:8px; font-size:13px; color:#475467; font-weight:500; }
  .au-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
  .au-status.is-submitted .au-dot { background:#12b76a; box-shadow:0 0 0 3px rgba(18,183,106,.15); }
  .au-status.is-draft .au-dot { background:#f79009; box-shadow:0 0 0 3px rgba(247,144,9,.15); }
  .au-actions { display:inline-flex; align-items:center; gap:8px; }
  .au-view-btn { background:#fff; border:1px solid var(--au-line); border-radius:8px; padding:8px 14px; font-size:12.5px; color:var(--au-ink); font-weight:600; white-space:nowrap; transition:all .15s; }
  .au-view-btn:hover { background:var(--au-primary); border-color:var(--au-primary); color:#fff; }
  .au-kebab.btn { width:30px; height:30px; padding:0; display:inline-flex; align-items:center; justify-content:center; color:#475467; border:0; border-radius:6px; }
  .au-kebab.btn::after { display:none; }
  .au-kebab.btn:hover, .au-kebab.btn:focus, .show > .au-kebab.btn { background:#f2f4f7; color:var(--au-ink); box-shadow:none; }
  .au-menu { font-size:13px; border-radius:8px; border-color:var(--au-line); box-shadow:0 8px 24px rgba(16,24,40,.12); min-width:180px; }
  .au-menu .dropdown-item { display:flex; align-items:center; gap:9px; padding:7px 14px; }
  .au-empty { text-align:center; color:var(--au-muted); padding:36px 0; font-size:13.5px; }
  .au-empty-icon { width:54px; height:54px; margin:0 auto 12px; border-radius:12px; background:#eef2ff; color:var(--au-primary); display:flex; align-items:center; justify-content:center; font-size:24px; }

  .au-pagination { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin:12px 12px 0; padding-top:14px; }
  .au-pagination-info { font-size:13px; color:var(--au-muted); }
  .au-pagination-controls { display:flex; align-items:center; gap:6px; }
  .au-pg { min-width:34px; height:34px; padding:0 8px; border:1px solid var(--au-line); background:#fff; border-radius:8px; font-size:13px; font-weight:600; color:#344054; display:inline-flex; align-items:center; justify-content:center; }
  .au-pg:hover:not(:disabled) { border-color:var(--au-primary); color:var(--au-primary); }
  .au-pg.is-on { background:var(--au-primary); border-color:var(--au-primary); color:#fff; }
  .au-pg:disabled { opacity:.5; cursor:not-allowed; }
  .au-pg-gap { color:#98a2b3; padding:0 4px; }
  .au-pagesize.form-select { width:112px; height:34px; font-size:13px; border-radius:8px; margin-left:6px; border-color:var(--au-line); }

  /* Drawer */
  .au-drawer.offcanvas { width:600px; max-width:100vw; border-left:0; box-shadow:-12px 0 40px rgba(16,24,40,.18); }
  .au-drawer-head { display:flex; align-items:center; gap:12px; padding:16px 20px; border-bottom:1px solid var(--au-line); background:linear-gradient(110deg,#f5f7ff,#fff); }
  .au-drawer-headmain { display:flex; align-items:center; gap:14px; flex:1; min-width:0; }
  .au-drawer-headtext { min-width:0; }
  .au-drawer-title { font-size:17px; font-weight:700; color:var(--au-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .au-drawer-sub { display:flex; align-items:center; flex-wrap:wrap; gap:8px; font-size:12.5px; color:var(--au-muted); margin-top:4px; }
  .au-sep { color:#d0d5dd; }
  .au-drawer-headactions { display:flex; gap:2px; flex-shrink:0; }
  .au-ibtn { width:32px; height:32px; border:0; background:none; border-radius:8px; color:#475467; display:inline-flex; align-items:center; justify-content:center; font-size:14px; }
  .au-ibtn:hover:not(:disabled) { background:#eef2ff; color:var(--au-primary); }
  .au-ibtn:disabled { opacity:.35; cursor:not-allowed; }
  .au-drawer-body { padding:18px 20px 8px; background:#f9fafb; }
  .au-drawer-status { display:flex; align-items:center; gap:12px; margin-bottom:14px; }
  .au-drawer-foot { display:flex; justify-content:flex-end; gap:10px; padding:12px 20px; border-top:1px solid var(--au-line); background:#fff; }

  .au-summary-strip { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; margin-bottom:12px; }
  .au-summary-pill { border:1px solid var(--au-line); border-radius:10px; padding:12px 14px; text-align:center; background:#fff; }
  .au-summary-pill.is-yes { background:#f0fdf6; border-color:#c9eedb; }
  .au-summary-pill.is-no { background:#fef4f4; border-color:#f6d0d0; }
  .au-summary-value { font-size:22px; font-weight:700; color:var(--au-ink); line-height:1.2; }
  .au-summary-pill.is-yes .au-summary-value { color:#12a05c; }
  .au-summary-pill.is-no .au-summary-value { color:#d92d20; }
  .au-summary-label { font-size:10.5px; color:var(--au-muted); text-transform:uppercase; letter-spacing:.06em; font-weight:600; margin-top:3px; }
  .au-rate { background:#fff; border:1px solid var(--au-line); border-radius:10px; padding:12px 14px; margin-bottom:12px; }
  .au-rate-head { display:flex; justify-content:space-between; font-size:12.5px; color:var(--au-muted); margin-bottom:8px; }
  .au-rate-head strong { color:var(--au-ink); }
  .au-rate-bar { height:8px; border-radius:6px; background:#fde2e1; overflow:hidden; }
  .au-rate-bar span { display:block; height:100%; background:linear-gradient(90deg,#12b76a,#32d583); border-radius:6px; }

  .au-meta-card { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px 16px; background:#fff; border:1px solid var(--au-line); border-radius:10px; padding:14px; margin-bottom:16px; }
  .au-meta-row { display:flex; align-items:flex-start; gap:10px; min-width:0; }
  .au-meta-row.is-wide { grid-column:1 / -1; }
  .au-meta-icon { width:30px; height:30px; flex-shrink:0; border-radius:8px; background:#eef2ff; color:var(--au-primary); display:inline-flex; align-items:center; justify-content:center; font-size:14px; }
  .au-meta-label { font-size:10.5px; font-weight:700; color:#98a2b3; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .au-meta-value { font-size:13.5px; color:var(--au-ink); font-weight:500; word-break:break-word; }
  .au-map-link { margin-left:10px; font-size:12px; color:var(--au-primary); display:inline-flex; align-items:center; gap:4px; white-space:nowrap; }

  .au-answers-bar { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:10px; }
  .au-seg { display:inline-flex; gap:6px; }
  .au-seg button { height:32px; padding:0 12px; font-size:12.5px; font-weight:600; color:#475467; background:#fff; border:1px solid #d0d5dd; border-radius:8px; display:inline-flex; align-items:center; gap:6px; }
  .au-seg button span { font-size:11px; color:#98a2b3; font-weight:700; }
  .au-seg button.is-on { background:#eef2ff; border-color:var(--au-primary); color:var(--au-primary); }
  .au-seg button.is-on.is-yes { background:#e6f7ee; border-color:#12b76a; color:#0f8a4c; }
  .au-seg button.is-on.is-no { background:#fef0ef; border-color:#d92d20; color:#b42318; }
  .au-seg button.is-on span { color:inherit; }

  .au-answer-group { margin-bottom:12px; border:1px solid var(--au-line); border-radius:10px; overflow:hidden; background:#fff; }
  .au-answer-group-head { width:100%; display:flex; align-items:center; gap:10px; padding:11px 14px; background:#fafbfc; border:0; text-align:left; }
  .au-answer-group-head:hover { background:#f3f5fa; }
  .au-answer-group-head > i { color:#98a2b3; font-size:12px; }
  .au-answer-group-title { flex:1; font-size:12px; font-weight:700; color:#344054; text-transform:uppercase; letter-spacing:.05em; }
  .au-group-badges { display:flex; gap:6px; }
  .au-group-badge { font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:20px; }
  .au-group-badge.is-yes { background:#e6f7ee; color:#0f8a4c; }
  .au-group-badge.is-no { background:#fef0ef; color:#d92d20; }
  .au-answer-list { border-top:1px solid #f0f2f5; }
  .au-answer-row { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; padding:11px 14px; border-bottom:1px solid #f4f5f7; font-size:13px; }
  .au-answer-row:last-child { border-bottom:0; }
  .au-answer-row.is-no { background:#fffafa; }
  .au-answer-task { color:#344054; min-width:0; }
  .au-answer-remark { font-size:12px; color:#667085; margin-top:6px; padding:6px 10px; background:#f9fafb; border-left:3px solid #d0d5dd; border-radius:0 6px 6px 0; }
  .au-answer-remark i { margin-right:4px; color:#98a2b3; }
  .au-answer-badge { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:700; padding:3px 10px; border-radius:20px; white-space:nowrap; flex-shrink:0; }
  .au-answer-badge.is-yes { background:#e6f7ee; color:#0f8a4c; }
  .au-answer-badge.is-no { background:#fef0ef; color:#d92d20; }
  .au-answer-badge.is-other { background:#f2f4f7; color:#667085; }

  /* Responsive */
  @media (max-width: 1399px) { .au-filter-grid { grid-template-columns:minmax(200px,1fr) repeat(3,minmax(120px,1fr)); } .au-search { grid-column:1 / -1; } }
  @media (max-width: 1199px) { .au-banner-art { display:none; } }
  @media (max-width: 991px) { .au-stats { grid-template-columns:1fr; } .au-page { padding:14px 12px 32px; } .au-filter-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
  @media (max-width: 767px) {
    .au-banner { flex-wrap:wrap; }
    .au-datepicker-input, .au-date-wrap, .au-date-wrap .react-datepicker-wrapper { width:100%; }
    .au-filter-actions { margin-left:0; width:100%; }
    .au-filter-actions .btn { flex:1; }
    .au-meta-card { grid-template-columns:1fr; }
    .au-pagination { justify-content:center; }
  }
`;

export default Audit;